import { Injectable, inject, signal, computed } from "@angular/core";
import { BehaviorSubject, Observable, switchMap, forkJoin, of, tap, map, distinctUntilChanged, filter, concatMap, EMPTY } from "rxjs";
import { Inspection } from "../../_objects/inspection";
import { SubjectModel } from "../../_objects/subject-model";
import { InspectionService } from "../http-api/inspection.service";
import { SubjectService } from "../http-api/subject.service";
import { Subject } from "../../_objects/subject";
import { SubjectModelService } from "../http-api/subject-model.service";
import { NetworkStatusService } from "../network-status.service";
import { InspectionImageStateService } from "../../inspection-data-management/inspection-images-state.service";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

type InspectionState = {
  inspection: Inspection;
  liveAsset: Subject;
  model: SubjectModel;
};

@Injectable({ providedIn: "root" })
export class InspectionStateService {
  private inspectionApiService = inject(InspectionService);
  private subjectApiService = inject(SubjectService);
  private subjectModelApiService = inject(SubjectModelService);
  private networkService = inject(NetworkStatusService);
  private inspectionImageStateService = inject(InspectionImageStateService);
  private state = signal<InspectionState | null>(null);

  public inspection = computed(() => this.state()?.inspection);
  public liveAsset = computed(() => this.state()?.liveAsset);
  public model = computed(() => this.state()?.model);

  private canEditInspectionSubject$ = new BehaviorSubject<boolean>(false);
  public canEditInspection$: Observable<boolean> = this.canEditInspectionSubject$.asObservable();

  constructor() {
    this.inspectionImageStateService.inspectionImages$
      .pipe(
        filter((a) => !!a),
        distinctUntilChanged((a, b) => a.length === b.length),
        takeUntilDestroyed(),
        concatMap(() => this.refresh())
      )
      .subscribe();
  }

  clearState(): void {
    this.state.set(null);
  }

  public init(inspectionId: string) {
    this.state.set(null);
    return this.refresh(inspectionId);
  }

  public refresh(inspectionId?: string) {
    const id = inspectionId ?? this.inspection().id;
    if (!id) {
      return EMPTY;
    }

    return this.inspectionApiService.getInspection(id).pipe(
      switchMap((inspection) =>
        forkJoin({
          subject: this.getLiveAsset(inspection),
          model: this.getModel(inspection),
          inspection: of(inspection)
        })
      ),
      tap(({ subject, inspection, model }) => {
        this.state.update(() => ({
          inspection: inspection,
          liveAsset: subject,
          model: model
        }));
      })
    );
  }

  private getLiveAsset(inspection: Inspection) {
    return this.subjectApiService.getSubject(inspection.subjectId);
  }

  private getModel(inspection: Inspection) {
    const model = this.model();
    const hasChanged = inspection.subjectModelId != model?.id;

    if (!hasChanged) {
      return of(model);
    }

    return this.networkService.isOnline
      ? this.subjectApiService.getSubject(inspection.subjectId).pipe(map((s) => s.subjectModel))
      : this.subjectModelApiService.getSubjectModel(inspection.subjectModelId);
  }

  setInspection(inspectionUpdated: Inspection) {
    this.state.update((s) => ({ ...s, inspection: inspectionUpdated }));
  }

  public getCanEditInspection(): Observable<boolean> {
    return this.canEditInspection$;
  }

  public setCanEditInspection(canEdit: boolean): void {
    this.canEditInspectionSubject$.next(canEdit);
  }
}
